import { AltLabel } from '../../TreeViewTypes';
import { DataNode, LabelContext } from '../../data_nodes/types';
import { LabelGenerator, DefaultLabelGenerator } from '../types';
import { Objective1, MilestoneMap1 } from '../../../types';
import {
  analyzeObjectiveStatus,
  countObjectiveStatuses,
  formatChildStatusCounts,
} from '../../../utils/statusSummaryUtils';
import { getChildObjectivesForParentObjectiveByOrder } from '../../../utils/objective-utils';
import {
  getVariantFromStatusCounts,
  getVariantFromStatusAnalysis,
  createVariantMetadata,
} from '../../utils/iconVariants';

/**
 * Helper function to safely collect all objectives from the tree structure
 * Uses safe traversal with null checking to prevent crashes
 */
function getAllObjectivesFromTree(rootNode: DataNode<any>): Objective1[] {
  const objectives: Objective1[] = [];

  function traverse(node: DataNode<any>) {
    // If this node has objective data, collect it
    if (node.data && (node.type === 'Objective' || node.type === 'MajorObjective')) {
      objectives.push(node.data as Objective1);
    }

    // ✅ Safe traversal with null checking to prevent crashes
    if (node.children && Array.isArray(node.children)) {
      node.children.forEach(traverse);
    }
  }

  traverse(rootNode);
  return objectives;
}

/**
 * ✅ Context-aware helper to find milestone map regardless of tree structure
 * Works in both single-map mode (MilestoneMap is root) and multi-map mode (Container is root)
 */
function findMilestoneMapInContext(context: LabelContext): MilestoneMap1 | null {
  // Try context root first (single-map mode)
  if (context.root.type === 'MilestoneMap') {
    return context.root.data as MilestoneMap1;
  }

  // Search in container children (multi-map mode)
  if (
    context.root.type === 'Container' &&
    context.root.children &&
    Array.isArray(context.root.children)
  ) {
    for (const child of context.root.children) {
      if (child.type === 'MilestoneMap') {
        return child.data as MilestoneMap1;
      }
    }
  }

  return null;
}

/**
 * Generate default label for Objective nodes (both regular and major objectives)
 *
 * Uses the comprehensive alt labels logic to ensure consistency.
 * Default format: [title] (green|yellow|red) - omits status if none.
 */
export const generateObjectiveDefaultLabel: DefaultLabelGenerator = (
  dataNode: DataNode<Objective1>,
  context: LabelContext,
): string => {
  const altLabels = generateObjectiveAltLabels(dataNode, context);
  const defaultLabel = altLabels.find((label) => label.key === 'default');
  return (
    defaultLabel?.value ||
    (dataNode.type === 'MajorObjective'
      ? 'Major Objective missing default label'
      : 'Objective missing default label')
  );
};

/**
 * Generate alt labels for Objective nodes
 *
 * Context-aware generator that adapts behavior based on tree structure:
 * - Dates tree: StatusReportDate is ancestor → show status for specific date
 * - Objectives tree: StatusReportDate is descendant → show latest status across all dates
 */
export const generateObjectiveAltLabels: LabelGenerator = (
  dataNode: DataNode<Objective1>,
  context: LabelContext,
): AltLabel[] => {
  // Context detection for tree-aware behavior
  const isStatusReportDateAncestorOfObjective = context.ancestors.some(
    (ancestor) => ancestor.type === 'StatusReportDate',
  );

  // For Major Objectives, check if we have Objective children (Objectives tree)
  // For regular Objectives, check if we have StatusReportDate children (Objectives tree)
  const isMajorObjective = dataNode.type === 'MajorObjective';
  const hasObjectiveChildren = context.allChildren.some((child) => child.type === 'Objective');
  const hasStatusReportDateChildren = context.allChildren.some(
    (child) => child.type === 'StatusReportDate',
  );

  const isInObjectivesTree = isMajorObjective ? hasObjectiveChildren : hasStatusReportDateChildren;

  if (isStatusReportDateAncestorOfObjective) {
    // Dates tree: StatusReportDate is ancestor → show status for this specific date
    return generateDatesTreeObjectiveLabels(dataNode, context);
  } else if (isInObjectivesTree) {
    // Objectives tree: StatusReportDate is descendant → show latest status across all dates
    // Objectives tree: in the traditional objectives hierarchy → show latest status across all dates
    return generateObjectivesTreeObjectiveLabels(dataNode, context);
  } else {
    // Unexpected case - log error and fallback
    console.warn('Unexpected tree structure in Objective generator:', {
      nodeType: dataNode.type,
      isMajorObjective,
      hasObjectiveChildren,
      hasStatusReportDateChildren,
      hasStatusReportDateAncestor: isStatusReportDateAncestorOfObjective,
      ancestors: context.ancestors.map((a) => a.type),
      children: context.allChildren.map((c) => c.type),
      context,
    });
    return generateFallbackObjectiveLabels(dataNode);
  }
};

/**
 * Generate labels for Objective in Dates tree (date-specific status)
 */
function generateDatesTreeObjectiveLabels(
  dataNode: DataNode<Objective1>,
  context: LabelContext,
): AltLabel[] {
  const objective = dataNode.data;

  // ✅ Validate input data
  if (!objective?.text) {
    const fallbackValue = dataNode.type === 'MajorObjective' ? 'Major Objective' : 'Objective';
    return [
      {
        key: 'default',
        selectOption: 'Default',
        value: fallbackValue,
      },
    ];
  }

  const title = objective.text || 'Untitled Objective';

  // TODO: Find status for specific date from ancestor StatusReportDate
  // For now, show placeholder while we figure out the date-specific status lookup

  return [
    {
      key: 'default',
      selectOption: 'Default: Title with date-specific status',
      value: `${title} (date-specific status not yet implemented)`,
    },
    {
      key: 'title-only',
      selectOption: 'Title-only',
      value: title,
    },
  ];
}

/**
 * Generate labels for Objective in Objectives tree (existing logic)
 */
function generateObjectivesTreeObjectiveLabels(
  dataNode: DataNode<Objective1>,
  context: LabelContext,
): AltLabel[] {
  const objective = dataNode.data;

  // ✅ Validate input data
  if (!objective?.text) {
    const fallbackValue = dataNode.type === 'MajorObjective' ? 'Major Objective' : 'Objective';
    return [
      {
        key: 'default',
        selectOption: 'Default',
        value: fallbackValue,
      },
    ];
  }

  const title = objective.text || 'Untitled Objective';
  const owner = objective.ownerName || 'no owner';
  const isMajorObjective = dataNode.type === 'MajorObjective';

  try {
    let statusColor = '';
    let statusCode = '';
    let childStatusSummary = '';
    let shortChildStatusSummary = '';

    if (isMajorObjective) {
      // For Major Objectives: show child status summary
      // ✅ Use context-aware milestone map detection
      const milestoneMap = findMilestoneMapInContext(context);
      // ✅ Safe objective collection - works for both tree types since it recursively finds all objectives
      const allObjectives = getAllObjectivesFromTree(context.root);

      if (milestoneMap && allObjectives && allObjectives.length > 0) {
        const childObjectives = getChildObjectivesForParentObjectiveByOrder(
          milestoneMap,
          allObjectives,
          objective.uuid,
        );

        const statusCounts = countObjectiveStatuses(childObjectives);
        childStatusSummary = formatChildStatusCounts(statusCounts, 'default');

        // ✅ Set icon variant based on status counts for Major Objectives
        const variant = getVariantFromStatusCounts(statusCounts);
        if (variant) {
          dataNode.metadata = { ...dataNode.metadata, ...createVariantMetadata(variant) };
        }

        // Also generate short format for later use
        shortChildStatusSummary = formatChildStatusCounts(statusCounts, 'short');

        // For Major Objectives, we'll use the child status summary
        if (childStatusSummary) {
          statusColor = childStatusSummary; // "3 green, 1 yellow, 1 red, 5 planned, 2 tbd"
        }
      }
    } else {
      // For regular Objectives: show own status
      const statusAnalysis = analyzeObjectiveStatus(objective);

      if (statusAnalysis.type !== 'none') {
        // Use the displayName and statusCode directly from the analysis
        statusColor = statusAnalysis.displayName; // "green", "yellow", "red", "planned", "TBD"
        statusCode = statusAnalysis.statusCode || ''; // "g", "y", "r", "p", "?"
      }

      // ✅ Set icon variant based on individual status for regular Objectives
      const variant = getVariantFromStatusAnalysis(statusAnalysis);
      if (variant) {
        dataNode.metadata = { ...dataNode.metadata, ...createVariantMetadata(variant) };
      }
    }

    const labels: AltLabel[] = [];

    if (isMajorObjective) {
      // Major Objective labels with child status summaries

      // Default: [title] (N green, N yellow, N red, N planned, N tbd) - omit status if none
      let defaultValue = title;
      if (statusColor) {
        defaultValue = `${title} (${statusColor})`;
      }

      labels.push({
        key: 'default',
        selectOption: 'Default: Title (N green, N yellow, N red, N planned, N tbd)',
        value: defaultValue,
      });

      // Short format: [title] (N g, N y, N r, N p, N tbd)
      let shortValue = title;
      if (shortChildStatusSummary) {
        shortValue = `${title} (${shortChildStatusSummary})`;
      }

      labels.push({
        key: 'short',
        selectOption: 'Short: Title (N g, N y, N r, N p, N tbd)',
        value: shortValue,
      });

      // Title-only: [title]
      labels.push({
        key: 'title-only',
        selectOption: 'Title-only',
        value: title,
      });
    } else {
      // Regular Objective labels with own status

      // Default: [title] (green|yellow|red) - omit status if none
      let defaultValue = title;
      if (statusColor) {
        defaultValue = `${title} (${statusColor})`;
      }

      labels.push({
        key: 'default',
        selectOption: 'Default: Title with status',
        value: defaultValue,
      });

      // Title, status, owner with labels: [title] (Status: [green]) - Owner: [Owner]
      let withLabelsValue = title;
      if (statusColor) {
        withLabelsValue = `${title} (Status: ${statusColor}) - Owner: ${owner}`;
      } else {
        withLabelsValue = `${title} - Owner: ${owner}`;
      }

      labels.push({
        key: 'with-labels',
        selectOption: 'Title, status, owner with labels',
        value: withLabelsValue,
      });

      // Title (status) - owner: [title] ([green]) - [Owner]
      let compactValue = title;
      if (statusColor) {
        compactValue = `${title} (${statusColor}) - ${owner}`;
      } else {
        compactValue = `${title} - ${owner}`;
      }

      labels.push({
        key: 'compact',
        selectOption: 'Title (status) - owner',
        value: compactValue,
      });

      // Short: [truncated title] (g|y|r) - omit status if none
      // TODO: Make truncation dynamic based on display width to prevent line wrapping
      const maxTitleLength = 30; // Reasonable default, should be made dynamic
      const truncatedTitle =
        title.length > maxTitleLength ? title.substring(0, maxTitleLength - 3) + '...' : title;

      let shortValue = truncatedTitle;
      if (statusCode) {
        shortValue = `${truncatedTitle} (${statusCode})`;
      }

      labels.push({
        key: 'short',
        selectOption: 'Short: Truncated title with status code',
        value: shortValue,
      });

      // Title-only: [title]
      labels.push({
        key: 'title-only',
        selectOption: 'Title-only',
        value: title,
      });
    }

    return labels;
  } catch (error) {
    console.warn(`Error generating ${isMajorObjective ? 'major ' : ''}objective labels:`, error);
    // ✅ Fallback to title-only labels if complex generation fails
    return [
      {
        key: 'default',
        selectOption: 'Default',
        value: title,
      },
      {
        key: 'title-only',
        selectOption: 'Title-only',
        value: title,
      },
    ];
  }
}

/**
 * Generate fallback labels when context detection fails
 */
function generateFallbackObjectiveLabels(dataNode: DataNode<Objective1>): AltLabel[] {
  const objective = dataNode.data;
  const fallbackValue = dataNode.type === 'MajorObjective' ? 'Major Objective' : 'Objective';
  const title = objective?.text || fallbackValue;

  return [
    {
      key: 'default',
      selectOption: 'Default',
      value: title,
    },
  ];
}
